import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Outlet, useLocation } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import Home from "@/pages/Home";
import Artists from "@/pages/Artists";
import GuestSpots from "@/pages/GuestSpots";
import NotFound from "@/pages/NotFound";
import LandingPage from "@/pages/LandingPage";
import AdminLogin from "@/pages/admin/AdminLogin";
import AuthCallback from "@/pages/admin/AuthCallback";
import AdminLayout, { RequireAuth } from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import PagesList from "@/pages/admin/PagesList";
import PageEditor from "@/pages/admin/PageEditor";
import PageStats from "@/pages/admin/PageStats";
import MediaLibrary from "@/pages/admin/MediaLibrary";
import Submissions from "@/pages/admin/Submissions";
import FormSettings from "@/pages/admin/FormSettings";
import AdminSettings from "@/pages/admin/AdminSettings";

/** Scrolls to top on route change, or to a #hash target when present. */
const ScrollManager = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const id = hash.replace("#", "");
      const t = setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
      return () => clearTimeout(t);
    }
    window.scrollTo({ top: 0, left: 0 });
  }, [pathname, hash]);
  return null;
};

/** Public marketing site shell (nav + footer). */
const SiteLayout = () => (
  <>
    <Navbar />
    <Outlet />
    <Footer />
  </>
);

const AppRoutes = () => {
  const location = useLocation();
  // Google sign-in returns to /admin#session_id=… — exchange it before any protected route runs.
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route element={<SiteLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/artists" element={<Artists />} />
        <Route path="/guest-spots" element={<GuestSpots />} />
        <Route path="*" element={<NotFound />} />
      </Route>

      {/* Promotional landing pages (no site nav) */}
      <Route path="/lp/:slug" element={<LandingPage />} />

      {/* Admin */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route
        path="/admin"
        element={
          <RequireAuth>
            <AdminLayout />
          </RequireAuth>
        }
      >
        <Route index element={<AdminDashboard />} />
        <Route path="pages" element={<PagesList />} />
        <Route path="pages/new" element={<PageEditor />} />
        <Route path="pages/:id" element={<PageEditor />} />
        <Route path="pages/:id/stats" element={<PageStats />} />
        <Route path="media" element={<MediaLibrary />} />
        <Route path="submissions" element={<Submissions />} />
        <Route path="forms" element={<FormSettings />} />
        <Route path="settings" element={<AdminSettings />} />
      </Route>
    </Routes>
  );
};

function App() {
  return (
    <div className="App min-h-screen bg-background text-foreground">
      <AuthProvider>
        <BrowserRouter>
          <ScrollManager />
          <AppRoutes />
          <Toaster position="bottom-right" richColors closeButton />
          <div className="grain" aria-hidden="true" />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
